import type { Callbacks, Directory, Member } from './directory.ts';
import type { NodeConnections, Nodes } from './types/rest.ts';
import type { MessageReceiver } from '../common/channel.ts';
import { getLogger } from '../logger.ts';

const logger = getLogger('gateway.mesh.rest-directory');

type MemberData = {
    node: string
    endpoint: string
    callbacks?: Callbacks
    users: Set<string>
    connected?: Member[]
    owners?: string[]
}

function fireCallbacks(nodeId: string, callbacks: Callbacks | undefined, toAdd: Member[], toRemove: Member[], newReplicas?: string[]) {
    const rcb = callbacks?.replicaChange;
    if (rcb && newReplicas) {
        rcb({cmd: 'replica-change', node: nodeId, replicas: newReplicas});
    }
    const dcb = callbacks?.disconnect;
    if (dcb) {
        toRemove.forEach((t) => dcb({cmd: 'disconnect', node: nodeId, from: t}));
    }

    const ccb = callbacks?.connect;
    if (ccb) {
        toAdd.forEach((t) => ccb({cmd: 'connect', node: nodeId, to: t}));
    }
}

type CommandAnnounce = {command: 'announce', nodes: Nodes};
type CommandResponse = {command: 'response', response: NodeConnections};
type CommandRemoveUsers = {command: 'remove-users', node: string, removed: string[]};
type CommandAddUsers = { command: 'add-users', node: string, added: string[] };
type CommandRemove = { command: 'remove', node: string };
type CommandAdd = { command: 'add', node: string, callbacks?: Callbacks, endpoint: string };
type Command
    = CommandAdd
    | CommandRemove
    | CommandAddUsers
    | CommandRemoveUsers
    | CommandAnnounce
    | CommandResponse
    ;

class RestDirectory implements Directory {
    private announceId?: ReturnType<typeof setTimeout>;

    constructor(
        private readonly handler: MessageReceiver<Command>,
        private readonly localNodes: Map<string, MemberData>,
        announceInterval: number) {

        if (announceInterval > 0) {
            const task = async () => {
                maybeAnnounceNodes(this.handler, this.localNodes);
                clearTimeout(this.announceId);
                this.announceId = setTimeout(task, announceInterval);
            }
            this.announceId = setTimeout(task, announceInterval);
        }
    }

    add(nodeId: string, endpointUri: string, callbacks?: Callbacks) {
        this.handler({command: 'add', node: nodeId, callbacks, endpoint: endpointUri});
    }

    remove(nodeId: string) {
        this.handler({command: 'remove', node: nodeId});
    }

    addUsers(nodeId: string, ...added: string[]) {
        this.handler({command: 'add-users', node: nodeId, added});
    }

    removeUsers(nodeId: string, ...removed: string[]) {
        this.handler({command: 'remove-users', node: nodeId, removed});
    }

    close() {
        if (this.announceId) {
            clearTimeout(this.announceId);
        }
    }
}

function removeNode(fetchFn: typeof fetch, directoryUri: string, node?: string) {
    if (node) {
        fetchFn(`${directoryUri}/api/nodes/${node}`, {method: 'delete'})
            .catch((err) => {
                logger.warn(`remove node request failed`, err);
            });
    }
}

async function parseAnnounceResponse(response: Response): Promise<NodeConnections> {
    if (response.ok) {
        const body = await response.json();
        return body as NodeConnections;
    }
    else {
        // use body in error?
        throw new Error(`${response.status} ${response.statusText}`);
    }
}

function processResponse(localNodes: Map<string, MemberData>, response: NodeConnections): Map<string, MemberData> {
    if (logger.enabledFor('debug')) {
        logger.debug(`processing response ${JSON.stringify(response)}`);
    }

    return response.reduce((n, r) => {
        const nodeId = r.node;
        const localNode = n.get(nodeId);
        if (localNode) {
            const existingReplicas = localNode.owners;
            const replicas = r.replicas ?? [];
            const newReplicas = (existingReplicas === undefined || (existingReplicas.join(',') !== replicas.join(','))) ? replicas : undefined;

            const connected: Member[] | undefined = localNode.connected;
            const proposed: Member[] | undefined = r.connect;
            const toRemove: Member[] = (connected ?? []).filter(c => {
                return proposed === undefined || proposed.find((p) => (p.node === c.node) && (p.endpoint === c.endpoint)) === undefined;
            });
            const toAdd: Member[] = (proposed ?? []).filter(p => {
                return connected === undefined || connected.find((c) => (p.node === c.node) && (p.endpoint === c.endpoint)) === undefined;
            });
            fireCallbacks(nodeId, localNode.callbacks, toAdd, toRemove, newReplicas);
            localNode.owners = replicas;
            localNode.connected = proposed;
            return n;
        }
        return n;
    }, localNodes);
}

function maybeAnnounceNodes(handler: MessageReceiver<Command>, localNodes: Map<string, MemberData>) {
    const data: Nodes = Array.from(localNodes.values())
        .map(v => {
            return {node: v.node, endpoint: v.endpoint, users: Array.from(v.users)};
        });
    if (data.length > 0) {
        handler({command: 'announce', nodes: data});
    }
}

function announceNodes(handler: MessageReceiver<Command>, fetchFn: typeof fetch, directoryUri: string, nodes: Nodes) {
    const body = JSON.stringify(nodes);
    if (logger.enabledFor('debug')) {
        logger.debug(`announcing nodes ${body}`);
    }
    fetchFn(`${directoryUri}/api/nodes`, {
        method: 'post',
        body,
        credentials: 'include',
        headers: [['content-type', 'application/json']]
    }).then(async (response) => {
        const body = await parseAnnounceResponse(response);
        handler({ command: 'response', response: body });
    }).catch(err => {
        logger.warn(`announce node request failed`, err);
    });
}

function processCommand(handler: MessageReceiver<Command>, directoryUri: string, localNodes: Map<string, MemberData>, cmd: Command, fetchFn: typeof fetch) {
    switch (cmd.command) {
        case 'add': {
            const {node, callbacks, endpoint} = cmd;
            localNodes.set(node, {
                node: node,
                callbacks,
                endpoint,
                users: new Set(),
            });
            // no need to announce it here, since no users
            //maybeAnnounceNodes
            break;
        }
        case 'remove': {
            const {node} = cmd;
            removeNode(fetchFn, directoryUri, node);
            localNodes.delete(node);
            break;
        }
        case 'add-users': {
            const {node, added} = cmd;
            const localNode = localNodes.get(node);
            if (localNode) {
                added.forEach(user => localNode.users.add(user));
                maybeAnnounceNodes(handler, localNodes);
            }
            break;
        }
        case 'remove-users': {
            const {node, removed} = cmd;
            const localNode = localNodes.get(node);
            if (localNode) {
                removed.forEach(user => localNode.users.delete(user));
                maybeAnnounceNodes(handler, localNodes);
            }
            break;
        }
        case 'announce': {
            const {nodes} = cmd;
            announceNodes(handler, fetchFn, directoryUri, nodes);
            break;
        }
        case 'response': {
            const {response} = cmd;
            processResponse(localNodes, response);
            break;
        }
    }
}

function handle(handler: MessageReceiver<Command>, directoryUri: string, localNodes: Map<string, MemberData>, msg: Command, fetchFn: typeof fetch) {
    processCommand(handler, directoryUri, localNodes, msg, fetchFn);
}

export type RestDirectoryOptions = FetchOptions & {}

export function restDirectory(config: { uri: string, announceInterval?: number }, opts?: RestDirectoryOptions): Directory {
    const directoryUri = config.uri;
    const announceInterval = config.announceInterval ?? 10000;
    const localNodes = new Map<string, MemberData>();
    const handler = (msg: Command) => {
        handle(handler, directoryUri, localNodes, msg, createFetch(opts));
    }
    logger.info(`rest directory with uri '${directoryUri}' and announce interval ${announceInterval}ms`);
    return new RestDirectory(handler, localNodes, announceInterval);
}

export type FetchOptions = {
    fetch?: typeof fetch,
    headers?: HeadersInit,
    getHeaders?: () => HeadersInit,
}
function createFetch(opts?: FetchOptions): typeof fetch {
    const fetchFn = (opts?.fetch ?? globalThis.fetch) ?? fetch;
    const originalFetch = fetchFn;
    if (opts?.getHeaders || opts?.headers) {
        return async (input: RequestInfo | URL, init?: RequestInit) => {
            const headers = new Headers(init?.headers);
            if (opts?.headers) {
                const extraHeaders = new Headers(opts.headers);
                extraHeaders.forEach((value, key) => {
                    headers.set(key, value);
                });
            }
            if (opts?.getHeaders) {
                const extraHeaders = new Headers(opts.getHeaders());
                extraHeaders.forEach((value, key) => {
                    headers.set(key, value);
                });
            }
            return await originalFetch(input, {...init, headers});
        }
    }
    return fetchFn;
}
